import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import {
  FaFileAlt,
  FaPuzzlePiece,
  FaExclamationTriangle,
  FaFileSignature,
  FaColumns,
  FaTasks,
  FaHighlighter,
  FaPenNib,
  FaFolderOpen,
  FaCalendarAlt,
  FaGavel,
  FaUsers,
  FaPencilRuler,
  FaTable,
  FaWpforms,
  FaComments,
  FaBalanceScale,
  FaShieldAlt,
  FaBuilding,
  FaArrowRight,
} from 'react-icons/fa';
import { WORKFLOW_FEATURES_PAGE } from '../config/content';
import styles from './Features.module.css';
import SEO from '../components/SEO';

const iconMap: Record<string, React.ElementType> = {
  FaFileAlt,
  FaPuzzlePiece,
  FaExclamationTriangle,
  FaFileSignature,
  FaColumns,
  FaTasks,
  FaHighlighter,
  FaPenNib,
  FaFolderOpen,
  FaCalendarAlt,
  FaGavel,
  FaUsers,
  FaPencilRuler,
  FaTable,
  FaWpforms,
  FaComments,
  FaBalanceScale,
  FaShieldAlt,
  FaBuilding,
};

interface FeatureMeta {
  id: string;
  title: string;
  shortDescription: string;
  icon: string;
  order: number;
}

const Reveal: React.FC<{
  children: React.ReactNode;
  className?: string;
  id?: string;
}> = ({ children, className, id }) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });

  return (
    <motion.div
      ref={ref}
      id={id}
      className={className}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
};

const WorkflowFeatures: React.FC = () => {
  const [features, setFeatures] = useState<FeatureMeta[]>([]);
  const [activeFeature, setActiveFeature] = useState<string | null>(null);
  const [featureHtml, setFeatureHtml] = useState('');
  const [loadingContent, setLoadingContent] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetch('/data/workflow-features/manifest.json')
      .then((res) => res.json())
      .then((data: FeatureMeta[]) => {
        setFeatures(data);
        if (data.length > 0) {
          setActiveFeature(data[0].id);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!activeFeature) return;
    setLoadingContent(true);
    fetch(`/data/workflow-features/${activeFeature}/content.html`)
      .then((res) => res.text())
      .then((html) => {
        setFeatureHtml(html);
        setLoadingContent(false);
        if (contentRef.current) {
          contentRef.current.scrollTop = 0;
        }
      })
      .catch(() => {
        setFeatureHtml(`<p>${WORKFLOW_FEATURES_PAGE.contentNotAvailable}</p>`);
        setLoadingContent(false);
      });
  }, [activeFeature]);

  return (
    <main className={styles.page}>
      <SEO path="/workflow-features" />
      <div className={`${styles.ambient} ${styles.ambient1}`} />
      <div className={`${styles.ambient} ${styles.ambient2}`} />

      <div className={styles.heroWrapper}>
        <motion.div
          className={styles.heroInner}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          <div className={styles.heroContent}>
            <div className={styles.heroText}>
              <div className={styles.heroBadge}>
                <div className={styles.badgeDot} />
                <span>{WORKFLOW_FEATURES_PAGE.hero.label}</span>
              </div>
              <h1 className={styles.sectionTitle}>
                {WORKFLOW_FEATURES_PAGE.hero.title}<em>{WORKFLOW_FEATURES_PAGE.hero.titleAccent}</em>
              </h1>
              <p className={styles.sectionSub}>{WORKFLOW_FEATURES_PAGE.hero.subtitle}</p>
            </div>
            <div className={styles.heroImageWrap}>
              <img
                src="/images/workflow-features/hero-banner.svg"
                alt="Workflow"
                className={styles.heroImage}
              />
            </div>
          </div>
        </motion.div>
      </div>

      <div className={styles.sectionDivider} />

      <section className={styles.section}>
        <Reveal className={styles.explorerLayout}>
          <div className={styles.featureList}>
            {features.map((feature) => {
              const IconComponent = iconMap[feature.icon] || FaFileAlt;
              const isActive = activeFeature === feature.id;
              return (
                <button
                  key={feature.id}
                  className={`${styles.featureListItem} ${isActive ? styles.featureListItemActive : ''}`}
                  onClick={() => setActiveFeature(feature.id)}
                >
                  <div className={`${styles.featureListIcon} ${isActive ? styles.featureListIconActive : ''}`}>
                    <IconComponent />
                  </div>
                  <div className={styles.featureListText}>
                    <span className={styles.featureListTitle}>{feature.title}</span>
                    <span className={styles.featureListDesc}>{feature.shortDescription}</span>
                  </div>
                  {isActive && <div className={styles.activeIndicator} />}
                </button>
              );
            })}
          </div>

          <div className={styles.featureContent} ref={contentRef}>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeFeature}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {loadingContent ? (
                  <div className={styles.featureLoading}>
                    <div className={styles.loadingDot} />
                    <div className={styles.loadingDot} />
                    <div className={styles.loadingDot} />
                  </div>
                ) : (
                  <div dangerouslySetInnerHTML={{ __html: featureHtml }} />
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </Reveal>
      </section>

      <div className={styles.sectionDivider} />

      <div className={styles.ctaSection}>
        <Reveal className={styles.ctaBanner}>
          <div>
            <h2>{WORKFLOW_FEATURES_PAGE.cta.title}</h2>
            <p>{WORKFLOW_FEATURES_PAGE.cta.subtitle}</p>
          </div>
          <div className={styles.ctaActions}>
            <a
              href={WORKFLOW_FEATURES_PAGE.cta.buttonLink}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.btnLarge}
              style={{ textAlign: 'center' }}
            >
              {WORKFLOW_FEATURES_PAGE.cta.button}
              <FaArrowRight className={styles.ctaButtonIcon} />
            </a>
          </div>
        </Reveal>
      </div>
    </main>
  );
};

export default WorkflowFeatures;
